import { ChangeEvent, Dispatch, DragEvent, RefObject, SetStateAction, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { MediaInputContext } from './context/media.context'
import { ETranslationNs, ICustomIconFile, IMediaFileWithId } from 'src/types'
import { mediaUploadValidation } from 'src/common/validations'
import { AppEvents } from 'src/constants'
import useStyles from './styles'

interface IMediaInputComponentProps {
  inputRef: RefObject<HTMLInputElement>
  setCropModalOpen: Dispatch<SetStateAction<boolean>>
  setImageToCrop: Dispatch<SetStateAction<ICustomIconFile | null>>
}

const mediaFormats = 'image/*, audio/*, video/*, .pdf'

const MediaInputComponent = ({
  inputRef,
  setCropModalOpen,
  setImageToCrop,
}: IMediaInputComponentProps) => {
  const [t] = useTranslation(ETranslationNs.Form)

  const { inputName } = useContext(MediaInputContext)
  const { setFieldValue } = useFormikContext<IMediaFileWithId>()

  const classes = useStyles({})

  const afterFileChoose = (files: File[] | undefined | null) => {
    if (files) {
      const urls: Promise<unknown>[] = []

      files.forEach((file) => {
        if (!file.type) return
        const valid = mediaUploadValidation.isValidSync(file.type)
        if (!valid) {
          const detail = { message: t('not-supported-format-notification.v') }

          document.dispatchEvent(new CustomEvent(AppEvents.HTTP_ERROR, { detail }))
          return
        }

        urls.push(
          new Promise((resolve) => {
            const fileReader = new FileReader()
            fileReader.onload = () => {
              if (fileReader.readyState === 2) {
                resolve(fileReader.result)
              }
            }
            fileReader.readAsDataURL(file)
          }),
        )
      })

      Promise.all(urls)
        .then((value) => {
          if (!value) return

          if (files[0]?.type?.includes('image')) {
            setCropModalOpen(true)
            setImageToCrop({ file: files[0], icon: value[0] as string })
          } else {
            setFieldValue('link', value[0])
            setFieldValue('file', files[0])
            setFieldValue('type', files[0].type ?? '')
          }
        })
        .catch((e) => {
          console.log(e, 'catch')
        })
    }
  }

  const handleDrop = (e: DragEvent<HTMLInputElement>) => {
    const files = e.dataTransfer.files && Array.from(e.dataTransfer.files)
    afterFileChoose(files)
  }

  const handleChooseFile = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files && Array.from(e.target.files)
    afterFileChoose(files)

    if (e.target) {
      e.target.value = ''
    }
  }

  return (
    <input
      onDragEnter={handleDrop}
      ref={inputRef}
      accept={mediaFormats}
      id={inputName}
      name={inputName}
      className={classes.dragDropArea}
      type="file"
      onChange={handleChooseFile}
    />
  )
}

export { MediaInputComponent }
